/**
 * BookListCard Component - Improved List Layout
 * 
 * Enhanced horizontal card with correct hierarchy:
 * - Title (main identifier, max 2 lines)
 * - Author + Location (secondary meta)
 * - Price (or FREE)
 * - Save button
 */

import React, { useState, memo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Heart, FileText, MapPin } from 'lucide-react';
import { formatMoney } from '../../utils/formatters';
import { trackOpen, trackSave } from '../../utils/tracker';
import { toAbsoluteUrl } from '../../lib/media';
import './BookListCard.css';

// Placeholder colors
const PLACEHOLDER_COLORS = [
  ['#3B82F6', '#1D4ED8'],
  ['#8B5CF6', '#6D28D9'],
  ['#10B981', '#047857'],
  ['#F59E0B', '#D97706'],
  ['#EF4444', '#DC2626'],
  ['#EC4899', '#BE185D'],
];

function getPlaceholderGradient(title) {
  const code = (title || 'B').charCodeAt(0) % PLACEHOLDER_COLORS.length;
  return PLACEHOLDER_COLORS[code];
}

function getInitials(title) {
  if (!title) return '?';
  const words = title.split(/\s+/).filter(Boolean);
  if (words.length >= 2) return (words[0][0] + words[1][0]).toUpperCase();
  return words[0].substring(0, 2).toUpperCase();
}

const BookListCard = memo(function BookListCard({
  book,
  isSaved = false,
  onSave,
  isRTL = false,
  lang = 'en',
}) {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [imageError, setImageError] = useState(false);
  
  const actualRTL = isRTL || i18n.language?.startsWith('ar');
  const actualLang = lang || (actualRTL ? 'ar' : 'en');
  
  // Title
  const title = actualRTL && book.title_ar ? book.title_ar : book.title;
  
  // Pricing
  const isFree = book.mode === 'free' || book.price === 0;
  const priceDisplay = isFree 
    ? (actualRTL ? 'مجاني' : 'Free')
    : formatMoney(book.price, book.currency || 'SDG', actualLang);
  
  // City label
  const getCityLabel = (cityKey) => {
    const cities = {
      khartoum: { en: 'Khartoum', ar: 'الخرطوم' },
      omdurman: { en: 'Omdurman', ar: 'أم درمان' },
      khartoum_north: { en: 'Bahri', ar: 'بحري' },
      port_sudan: { en: 'Port Sudan', ar: 'بورتسودان' },
    };
    const city = cities[cityKey];
    return city ? (actualRTL ? city.ar : city.en) : cityKey;
  };
  
  // Placeholder
  const [gradStart, gradEnd] = getPlaceholderGradient(title);
  const initials = getInitials(title);
  
  const handleCardClick = () => {
    trackOpen(book.id, book.category || 'books_notes');
    navigate(`/student-hub/books/${book.id}`, { state: { book } });
  };
  
  const handleSaveClick = (e) => {
    e.stopPropagation();
    trackSave(book.id, book.category || 'books_notes', !isSaved);
    onSave?.(book.id);
  };

  return (
    <article
      className="book-list-card"
      onClick={handleCardClick}
      dir={actualRTL ? 'rtl' : 'ltr'}
      data-testid={`book-list-${book.id}`}
    >
      {/* Thumbnail */}
      <div className="blc-thumbnail">
        {(book.images?.[0] || book.imageUrl) && !imageError ? (
          <img
            src={toAbsoluteUrl(book.images?.[0] || book.imageUrl)}
            alt={title}
            onError={() => setImageError(true)}
          />
        ) : (
          <div 
            className="blc-placeholder"
            style={{ background: `linear-gradient(135deg, ${gradStart}, ${gradEnd})` }}
          >
            <span className="blc-initials">{initials}</span>
            <FileText size={12} className="blc-file-icon" />
          </div>
        )}
      </div>

      {/* Content */}
      <div className="blc-content">
        {/* Title - main identifier */}
        <h3 className="blc-title">{title}</h3>
        
        {/* Meta: Author + Location */}
        <div className="blc-meta">
          {book.author && <span className="blc-author">{book.author}</span>}
          {book.city && (
            <span className="blc-city">
              <MapPin size={11} />
              {getCityLabel(book.city)}
            </span>
          )}
        </div>

        {/* Price */}
        <span className={`blc-price ${isFree ? 'free' : ''}`}>
          {priceDisplay}
        </span>
      </div>

      {/* Save button */}
      <button
        className={`blc-save ${isSaved ? 'saved' : ''}`}
        onClick={handleSaveClick}
        aria-label={actualRTL ? 'حفظ' : 'Save'}
        data-testid={`save-list-${book.id}`}
      >
        <Heart size={18} fill={isSaved ? 'currentColor' : 'none'} />
      </button>
    </article>
  );
});

export default BookListCard;
